import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PaperPlaneTilt, Spinner } from "@phosphor-icons/react";
import { API } from "@/lib/site";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error();
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your email first.");
    setSubscribing(true);
    try {
      const res = await fetch(`${API}/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error();
      toast.success(data.message || "Subscribed!");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setSubscribing(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-brand-line py-3 text-brand-text placeholder:text-brand-dim/50 focus:outline-none focus:border-brand-gold transition-colors";

  return (
    <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-16">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="lg:col-span-5"
      >
        <span className="overline">Start the conversation</span>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-brand-text mt-4 mb-6">
          Let's build your better life.
        </h2>
        <p className="text-brand-dim text-lg leading-relaxed mb-10 max-w-md">
          Tell me where you are and where you'd like to be. Every message is
          read personally by the TonyBrand29 team.
        </p>

        <div className="rounded-3xl border border-brand-line bg-brand-surface p-8">
          <p className="font-serif text-2xl text-brand-text mb-2">The weekly note</p>
          <p className="text-brand-dim text-sm mb-6">
            One short, practical email on health, wealth and mindset. No spam.
          </p>
          <form onSubmit={subscribe} className="flex gap-3">
            <input
              data-testid="subscribe-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className={field + " flex-1"}
            />
            <button
              data-testid="subscribe-submit"
              type="submit"
              disabled={subscribing}
              className="rounded-full bg-brand-sage px-6 py-3 text-sm font-medium text-brand-bg hover:bg-brand-gold transition-colors disabled:opacity-60"
            >
              {subscribing ? "…" : "Join"}
            </button>
          </form>
        </div>
      </motion.div>

      <motion.form
        onSubmit={submit}
        data-testid="contact-form"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className="lg:col-span-7 rounded-3xl border border-brand-line bg-brand-surface p-8 lg:p-12 space-y-8"
      >
        <div className="grid sm:grid-cols-2 gap-8">
          <div>
            <label className="overline block mb-2">Your name</label>
            <input
              data-testid="contact-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jordan Rivera"
              className={field}
            />
          </div>
          <div>
            <label className="overline block mb-2">Email</label>
            <input
              data-testid="contact-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="jordan@email.com"
              className={field}
            />
          </div>
        </div>
        <div>
          <label className="overline block mb-2">How can Tony help?</label>
          <textarea
            data-testid="contact-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            rows={5}
            placeholder="Tell me a little about your goals…"
            className={field + " resize-none"}
          />
        </div>
        <button
          data-testid="contact-submit"
          type="submit"
          disabled={sending}
          className="inline-flex items-center gap-2 rounded-full bg-brand-text px-8 py-4 text-sm font-medium text-brand-bg hover:bg-brand-gold transition-colors disabled:opacity-60"
        >
          {sending ? (
            <>
              <Spinner size={18} className="animate-spin" /> Sending
            </>
          ) : (
            <>
              Send message <PaperPlaneTilt size={18} />
            </>
          )}
        </button>
      </motion.form>
    </section>
  );
};
